import apiService from '../services/apiService';
import Device from '../models/Device';

export const deviceController = {
  // Get all devices with optional filters
  getAll: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const endpoint = queryParams ? `devices?${queryParams}` : 'devices';
      const response = await apiService.get(endpoint);
      const devices = response.data?.map(d => Device.fromJSON(d)) || [];
      return { success: true, data: devices, total: response.total || devices.length };
    } catch (error) {
      return { success: false, data: [], message: error.message || 'Failed to fetch devices' };
    }
  },

  // Get device by ID
  getById: async (id) => {
    try {
      const response = await apiService.get(`devices/${id}`);
      return { success: true, data: Device.fromJSON(response.data) };
    } catch (error) {
      return { success: false, data: null, message: error.message };
    }
  },

  // Create new device
  create: async (deviceData) => {
    try {
      const device = new Device(deviceData);
      const validation = device.validate();
      if (!validation.isValid) return { success: false, errors: validation.errors };
      const response = await apiService.post('devices', device.toJSON());
      return { success: true, data: Device.fromJSON(response.data), message: 'Device enrolled successfully' };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Update device
  update: async (id, deviceData) => {
    try {
      const device = new Device({ ...deviceData, id });
      const validation = device.validate();
      if (!validation.isValid) return { success: false, errors: validation.errors };
      const response = await apiService.put(`devices/${id}`, device.toJSON());
      return { success: true, data: Device.fromJSON(response.data), message: 'Device updated successfully' };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Delete device
  delete: async (id) => {
    try {
      await apiService.delete(`devices/${id}`);
      return { success: true, message: 'Device removed successfully' };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Update device status
  updateStatus: async (id, status) => {
    try {
      const response = await apiService.patch(`devices/${id}/status`, { status });
      return { success: true, data: Device.fromJSON(response.data), message: 'Status updated' };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Remote lock device
  remoteLock: async (id) => {
    try {
      const response = await apiService.post(`devices/${id}/lock`);
      return { success: true, data: response.data, message: 'Device locked remotely' };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Remote wipe device
  remoteWipe: async (id) => {
    try {
      const response = await apiService.post(`devices/${id}/wipe`);
      return { success: true, data: response.data, message: 'Device wiped remotely' };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Assign device to employee
  assign: async (id, employeeId) => {
    try {
      const response = await apiService.patch(`devices/${id}/assign`, { employeeId });
      return { success: true, data: Device.fromJSON(response.data), message: 'Device assigned successfully' };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Get devices by platform
  getByPlatform: async (platform) => {
    return await deviceController.getAll({ platform });
  },

  // Get non-compliant devices
  getNonCompliant: async () => {
    return await deviceController.getAll({ complianceStatus: 'Non-Compliant' });
  },

  // Export devices
  export: async (filters = {}, format = 'csv') => {
    try {
      const queryParams = new URLSearchParams({ ...filters, format }).toString();
      const response = await apiService.get(`devices/export?${queryParams}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Get statistics
  getStatistics: async () => {
    try {
      const response = await apiService.get('devices/statistics');
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, data: null, message: error.message };
    }
  },
};

export default deviceController;